import { useEffect, useCallback, useState } from "react";

/**
 * Custom hook to manage browser navigation in SPA
 * Prevents users from accidentally leaving the application
 * and provides controlled navigation within the app
 */
export const useNavigationGuard = ({
  onNavigateBack = null,
  preventBrowserBack = true,
  confirmMessage = "Are you sure you want to leave KinShield? Any unsaved changes will be lost.",
}) => {
  // Handle browser back button
  const handlePopState = useCallback(
    (event) => {
      console.log("NavigationGuard: Browser back button detected");

      if (onNavigateBack && typeof onNavigateBack === "function") {
        // Call custom back handler if provided
        onNavigateBack(event);
      } else if (preventBrowserBack) {
        // Prevent browser back and stay in app
        window.history.pushState(null, "", window.location.pathname);

        // Optional: Show confirmation dialog
        if (confirmMessage && window.confirm(confirmMessage)) {
          // User confirmed they want to leave
          window.history.back();
        }
      }
    },
    [onNavigateBack, preventBrowserBack, confirmMessage]
  );

  // Handle page unload/refresh
  const handleBeforeUnload = useCallback(
    (event) => {
      console.log("NavigationGuard: Page unload detected");
      event.preventDefault();
      event.returnValue = confirmMessage;
      return confirmMessage;
    },
    [confirmMessage]
  );

  useEffect(() => {
    // Push initial state to prevent immediate back navigation
    if (preventBrowserBack) {
      window.history.pushState(null, "", window.location.pathname);
    }

    // Add event listeners
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Cleanup event listeners
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handlePopState, handleBeforeUnload, preventBrowserBack]);

  // Utility functions for controlled navigation
  const navigateWithinApp = useCallback((path, state = {}) => {
    window.history.pushState(state, "", path);
    console.log(`NavigationGuard: Navigated to ${path}`);
  }, []);

  const replaceCurrentState = useCallback((path, state = {}) => {
    window.history.replaceState(state, "", path);
    console.log(`NavigationGuard: Replaced current state with ${path}`);
  }, []);

  const safeExit = useCallback(() => {
    // Disable the guard temporarily for intentional navigation
    window.removeEventListener("popstate", handlePopState);
    window.removeEventListener("beforeunload", handleBeforeUnload);

    // Navigate to safe exit point (like main site)
    window.location.href = "/";
  }, [handlePopState, handleBeforeUnload]);

  return {
    navigateWithinApp,
    replaceCurrentState,
    safeExit,
  };
};

/**
 * Specialized hook for SPA dashboard navigation
 */
export const useDashboardNavigation = () => {
  const [navigationStack, setNavigationStack] = useState(["dashboard"]);

  const navigateToSection = useCallback((section) => {
    setNavigationStack((prev) => [...prev, section]);
    window.history.pushState({ section }, "", `#${section}`);
    console.log(`Dashboard Navigation: Moved to ${section}`);
  }, []);

  const navigateBack = useCallback(() => {
    if (navigationStack.length > 1) {
      const newStack = navigationStack.slice(0, -1);
      const previousSection = newStack[newStack.length - 1];

      setNavigationStack(newStack);
      window.history.pushState(
        { section: previousSection },
        "",
        `#${previousSection}`
      );

      console.log(`Dashboard Navigation: Back to ${previousSection}`);
      return previousSection;
    } else {
      console.log(
        "Dashboard Navigation: Already at root, staying in dashboard"
      );
      return "dashboard";
    }
  }, [navigationStack]);

  const getCurrentSection = useCallback(() => {
    return navigationStack[navigationStack.length - 1] || "dashboard";
  }, [navigationStack]);

  // Handle browser back button for dashboard navigation
  const handleDashboardBack = useCallback(
    (event) => {
      event.preventDefault();
      const previousSection = navigateBack();

      // Dispatch custom event to inform components of navigation change
      window.dispatchEvent(
        new CustomEvent("dashboardNavigationChange", {
          detail: { section: previousSection },
        })
      );
    },
    [navigateBack]
  );

  return {
    navigateToSection,
    navigateBack,
    getCurrentSection,
    handleDashboardBack,
    navigationStack: [...navigationStack],
  };
};

export default useNavigationGuard;
